import React, { useState } from "react";
import { WhyWhyAnalysisEntry } from "./types";

interface WhyWhyAnalysisComponentProps {
  whyWhyAnalysis: WhyWhyAnalysisEntry[];
  onChange: (updated: WhyWhyAnalysisEntry[]) => void;
  readOnly?: boolean;
}

const WhyWhyAnalysisComponent: React.FC<WhyWhyAnalysisComponentProps> = ({
  whyWhyAnalysis,
  onChange,
  readOnly = false,
}) => {
  const [rootProblem, setRootProblem] = useState("");

  const handleAddWhy = (parentLevel: number = 0) => {
    const newEntry: WhyWhyAnalysisEntry = {
      question: "",
      answer: "",
      level: parentLevel + 1,
    };
    onChange([...whyWhyAnalysis, newEntry]);
  };

  const handleUpdateEntry = (
    index: number,
    field: keyof WhyWhyAnalysisEntry,
    value: string | number
  ) => {
    const updated = [...whyWhyAnalysis];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleRemoveEntry = (index: number) => {
    const updated = whyWhyAnalysis.filter((_, i) => i !== index);
    onChange(updated);
  };

  const getEntriesByLevel = (level: number) => {
    return whyWhyAnalysis.filter((entry) => entry.level === level);
  };

  const renderWhyChain = (level: number, _parentIndex?: number) => {
    const entries = getEntriesByLevel(level);
    if (entries.length === 0 && level === 1) {
      return (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => handleAddWhy(0)}
            className="px-4 py-2 bg-[#0fa9a6] text-white rounded hover:bg-[#0d8f8c]"
            disabled={readOnly}
          >
            Start Why-Why Analysis
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {entries.map((entry) => {
          const globalIndex = whyWhyAnalysis.findIndex((e) => e === entry);
          return (
            <div
              key={globalIndex}
              className={`border-l-4 ${
                level === 1
                  ? "border-blue-500"
                  : level === 2
                  ? "border-green-500"
                  : level === 3
                  ? "border-yellow-500"
                  : level === 4
                  ? "border-orange-500"
                  : "border-red-500"
              } pl-4 py-2 bg-gray-50 rounded-r`}
            >
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">
                  Why {level}:
                </label>
                <input
                  type="text"
                  value={entry.question}
                  onChange={(e) =>
                    handleUpdateEntry(globalIndex, "question", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  placeholder={`Why did this happen? (Level ${level})`}
                  readOnly={readOnly}
                />
              </div>
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">Answer:</label>
                <textarea
                  value={entry.answer}
                  onChange={(e) =>
                    handleUpdateEntry(globalIndex, "answer", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  rows={2}
                  placeholder="Answer to the why question"
                  readOnly={readOnly}
                />
              </div>
              <div className="flex gap-2">
                {!readOnly && level < 5 && (
                  <button
                    type="button"
                    onClick={() => handleAddWhy(level)}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                  >
                    Ask Why {level + 1}
                  </button>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEntry(globalIndex)}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              {level < 5 && renderWhyChain(level + 1, globalIndex)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-green-50 p-4 rounded-lg">
        <h4 className="font-semibold text-green-900 mb-2">
          Why-Why Analysis (5 Whys)
        </h4>
        <p className="text-sm text-green-800">
          Why-Why analysis, also known as the "5 Whys" method, is a problem-solving
          technique used to identify root causes by repeatedly asking "why" until you
          reach the fundamental reason for a problem.
        </p>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Root Problem / Initial Event
        </label>
        <textarea
          value={rootProblem}
          onChange={(e) => setRootProblem(e.target.value)}
          className="w-full p-2 border rounded"
          rows={2}
          placeholder="Describe the initial problem or event"
          readOnly={readOnly}
        />
      </div>

      <div className="space-y-4">{renderWhyChain(1)}</div>

      {whyWhyAnalysis.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded">
          <h5 className="font-semibold mb-2">Analysis Summary</h5>
          <div className="space-y-2">
            {whyWhyAnalysis
              .sort((a, b) => a.level - b.level)
              .map((entry, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium">Level {entry.level}:</span>{" "}
                  {entry.question && (
                    <>
                      <span className="text-gray-600">Why: {entry.question}</span>
                      {entry.answer && (
                        <>
                          {" "}
                          <span className="text-gray-800">
                            → Answer: {entry.answer}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhyWhyAnalysisComponent;

